import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { Backspace, Refresh } from "@mui/icons-material";
import EmailPreviewDialog from "../Components/EmailPreviewDialog";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography,
  Chip,
  Tooltip,
  LinearProgress,
  Alert,
} from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import Page from "./Page";
import {
  GET_MY_AUTH,
  GET_APPLICANTS,
  GET_EMAIL_TEMPLATES,
  SEND_BULK_COUPON_EMAILS,
} from "../graphql/queries";
import DataTable from "../Components/DataTable";
import ClaimsFilterPanel from "../Components/ClaimsFilterPanel";
import { formatDate, parseJsonData } from "../utils/dateUtils";
import { API_URL } from "../constants";
import { COUPON_CONFIG } from "../constants/couponConstants";

const API_BASE = API_URL.replace(/\/graphql\/?$/i, "");
const INVOICE_BASE_URL = "https://api-leadgen.eventegration.com";
const isCouponEvent = (eventId) => eventId === COUPON_CONFIG.EVENT_ID;

export const GET_ME = gql`
  query {
    me
  }
`;

export default function MyVisitorsPage() {
  const [search, setSearch] = React.useState("");
  const [emailPreviewOpen, setEmailPreviewOpen] = React.useState(false);
  const [emailPreviewRows, setEmailPreviewRows] = React.useState([]);
  const [notification, setNotification] = React.useState({ open: false, message: "", severity: "info" });
  const [claimFilterShopName, setClaimFilterShopName] = React.useState("");
  const [claimFilterShopLocation, setClaimFilterShopLocation] = React.useState("");
  const [claimFilterDateFrom, setClaimFilterDateFrom] = React.useState("");
  const [claimFilterDateTo, setClaimFilterDateTo] = React.useState("");
  const {
    data: authData,
    loading: authLoading,
    error: authError,
  } = useQuery(GET_MY_AUTH);

  const isAdmin = authData && authData?.me && authData?.me === "admin";
  // console.log(isAdmin);
  // if (authLoading) return "Please wait...";

  const { data: templatesData } = useQuery(GET_EMAIL_TEMPLATES, {
    variables: { templateType: "coupon_success" },
  });
  const [sendBulkCouponEmails] = useMutation(SEND_BULK_COUPON_EMAILS);
  const {
    data: applicantsData,
    loading: applicantsLoading,
    error: applicantsError,
    refetch,
  } = useQuery(GET_APPLICANTS, {
    variables: {
      conditions: { limit: 5000 },
      role: "all",
    },
    skip: !authData?.eventId || !authData?.standId,
    fetchPolicy: "network-only",
  });
  const loading = authLoading || applicantsLoading;
  const error = authError || applicantsError;
  const eventId = authData?.eventId || "";

  const handleViewInvoice = (row) => {
    if (!row.invoicePath) return;
    const url =
      row.invoicePath.startsWith("http") ?
        row.invoicePath
      : `${API_BASE}/uploads/invoices/${eventId}/${(row.invoicePath || "").split("/").pop()}`;
    window.open(url, "_blank");
  };

  const couponEventColumns = [
    {
      field: "data",
      headerName: "Contact Details",
      minWidth: 220,
    },
    {
      field: "couponNo",
      headerName: "Coupon #",
      width: 120,
    },
    {
      field: "totalBill",
      headerName: "Total Bill",
      width: 100,
      renderCell: ({ row }) =>
        row.totalBill != null ? Number(row.totalBill).toFixed(2) : "—",
    },
    {
      field: "discountApplied",
      headerName: "Discount Amount",
      width: 120,
      renderCell: ({ row }) =>
        row.discountApplied != null ? Number(row.discountApplied).toFixed(2) : "—",
    },
    {
      field: "finalAmount",
      headerName: "Final Amount",
      width: 110,
      renderCell: ({ row }) =>
        row.finalAmount != null ? Number(row.finalAmount).toFixed(2) : "—",
    },
    {
      field: "shopName",
      headerName: "Claimed by Shop",
      minWidth: 160,
    },
    {
      field: "shopLocation",
      headerName: "Shop Location",
      minWidth: 140,
    },
    {
      field: "shopContact",
      headerName: "Shop Contact",
      width: 130,
    },
    {
      field: "invoicePath",
      headerName: "View invoice",
      width: 110,
      renderCell: ({ row }) =>
        row.invoicePath ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              handleViewInvoice(row);
            }}
          >
            View
          </Button>
        ) : (
          "—"
        ),
    },
    {
      field: "created",
      headerName: "Coupon claimed Date",
      minWidth: 160,
    },
  ];

  const defaultColumns = [
    {
      field: "id",
      headerName: "Badge ID",
      width: 120,
    },
    {
      field: "name",
      headerName: "Name",
      minWidth: 180,
    },
    {
      field: "role",
      headerName: "Role",
      width: 120,
      renderCell: ({ row }) => (
        <Chip
          label={row.role}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: "type",
      headerName: "Lead Type",
      width: 130,
      renderCell: ({ row }) => (
        <Chip
          label={row.type}
          size="small"
          color={
            row.type === "Hot Lead"
              ? "error"
              : row.type === "Warm Lead"
              ? "warning"
              : row.type === "Cold Lead"
              ? "info"
              : "default"
          }
          variant="outlined"
        />
      ),
    },
    {
      field: "remark",
      headerName: "Remarks",
      minWidth: 200,
    },
    {
      field: "data",
      headerName: "Contact Details",
      minWidth: 220,
    },
    {
      field: "couponNo",
      headerName: "Coupon #",
      width: 120,
    },
    {
      field: "totalBill",
      headerName: "Bill (OMR)",
      width: 100,
      renderCell: ({ row }) =>
        row.totalBill != null ? Number(row.totalBill).toFixed(2) : "—",
    },
    {
      field: "invoicePath",
      headerName: "Invoice",
      width: 100,
      renderCell: ({ row }) =>
        row.invoicePath && eventId ? (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              handleViewInvoice(row);
            }}
          >
            View
          </Button>
        ) : (
          "—"
        ),
    },
    {
      field: "created",
      headerName: "Scanned On",
      minWidth: 160,
    },
  ];

  // For attendee in coupon event: hide shop name, shop location, shop contact
  const columns = isCouponEvent(eventId)
    ? isAdmin
      ? couponEventColumns
      : couponEventColumns.filter(
          (col) =>
            !["shopName", "shopLocation", "shopContact"].includes(col.field),
        )
    : defaultColumns;

  const handleRefresh = () => {
    refetch();
  };

  const couponSuccessTemplate = templatesData?.getEmailTemplates?.[0] || null;

  const handleSendEmailClick = (selectedRows) => {
    setEmailPreviewRows(selectedRows);
    setEmailPreviewOpen(true);
  };

  const handleSendCouponEmails = async () => {
    const claims = emailPreviewRows
      .filter((r) => r.id)
      .map((r) => ({
        id: r.id,
        role: r.role || "attendee",
        couponNo: r.couponNo ?? r.coupon_no ?? "",
        totalBill: r.totalBill ?? r.total_bill ?? 0,
        discountApplied: r.discountApplied ?? r.discount_applied ?? 0,
        grandTotal: r.grandTotal ?? r.finalAmount ?? r.grand_total ?? 0,
        standId: r.standId ?? r.stand_id ?? null,
        scanName: r.shopName ?? r.scan_name ?? r.name ?? "",
        userData: r.data ? (typeof r.data === "string" ? r.data : JSON.stringify(r.data)) : (r.parsedData ? JSON.stringify(r.parsedData) : null),
      }));
    if (claims.length === 0) throw new Error("No claims selected");
    const result = await sendBulkCouponEmails({ variables: { claims } });
    return result.data?.sendBulkCouponEmails || "Emails sent";
  };

  const tableData = (applicantsData?.applicants || []).map((item) => {
    const parsed = parseJsonData(item.data);
    const base = {
      ...item,
      formattedCreated: formatDate(item.created),
      parsedData: parsed,
    };
    if (isCouponEvent(eventId)) {
      const discount = item.discountApplied ?? 0;
      const bill = item.totalBill ?? 0;
      return {
        ...base,
        finalAmount: item.grandTotal != null ? item.grandTotal : (Number(bill) - Number(discount)),
        invoicePath: item.invoicePath
          ? `${INVOICE_BASE_URL}/${(item.invoicePath + "").replace(/^\//, "")}`
          : item.invoicePath,
      };
    }
    return base;
  });

  const couponShopNames = React.useMemo(
    () =>
      isCouponEvent(eventId)
        ? tableData.map((r) => (r.shopName || "").trim()).filter(Boolean)
        : [],
    [eventId, tableData],
  );
  const couponShopLocations = React.useMemo(
    () =>
      isCouponEvent(eventId)
        ? tableData.map((r) => (r.shopLocation || "").trim()).filter(Boolean)
        : [],
    [eventId, tableData],
  );
  const filteredTableData = React.useMemo(() => {
    if (!isCouponEvent(eventId)) return tableData;
    let list = tableData;
    if (claimFilterShopName) {
      list = list.filter((r) => (r.shopName || "").trim() === claimFilterShopName);
    }
    if (claimFilterShopLocation) {
      list = list.filter(
        (r) => (r.shopLocation || "").trim() === claimFilterShopLocation,
      );
    }
    if (claimFilterDateFrom) {
      const fromStart = new Date(claimFilterDateFrom);
      fromStart.setHours(0, 0, 0, 0);
      list = list.filter(
        (r) => r.created && new Date(r.created) >= fromStart,
      );
    }
    if (claimFilterDateTo) {
      const toEnd = new Date(claimFilterDateTo);
      toEnd.setHours(23, 59, 59, 999);
      list = list.filter(
        (r) => r.created && new Date(r.created) <= toEnd,
      );
    }
    return list;
  }, [
    eventId,
    tableData,
    claimFilterShopName,
    claimFilterShopLocation,
    claimFilterDateFrom,
    claimFilterDateTo,
  ]);

  return (
    <Page title="My Scanned Data">
      <Typography variant="h4" gutterBottom>
        {isCouponEvent(eventId)
          ? "Scanned Coupon Claim Data"
          : isAdmin
          ? "Scanned Lead Data"
          : "My Scanned Data"}
      </Typography>
      {error ? (
        <Box
          sx={{
            width: "100%",
            minHeight: "55vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h2" color="darkred">
            We have encountered a glitch. We kindly request you to try again
            later. {error && (error.message || String(error))}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {isCouponEvent(eventId) && isAdmin && (
            <ClaimsFilterPanel
              shopNames={couponShopNames}
              shopLocations={couponShopLocations}
              shopName={claimFilterShopName}
              shopLocation={claimFilterShopLocation}
              dateFrom={claimFilterDateFrom}
              dateTo={claimFilterDateTo}
              onShopNameChange={setClaimFilterShopName}
              onShopLocationChange={setClaimFilterShopLocation}
              onDateFromChange={setClaimFilterDateFrom}
              onDateToChange={setClaimFilterDateTo}
              onClear={() => {
                setClaimFilterShopName("");
                setClaimFilterShopLocation("");
                setClaimFilterDateFrom("");
                setClaimFilterDateTo("");
              }}
            />
          )}
          <DataTable
            title={isCouponEvent(eventId) ? "Coupon Claim" : "Lead Management"}
            isAdmin={isAdmin}
            columns={columns}
            data={isCouponEvent(eventId) ? filteredTableData : tableData}
            loading={loading}
            onRefresh={handleRefresh}
            exportFilename={isCouponEvent(eventId) ? "Coupon Claim" : "My_Leads"}
            exportDataKeyPrefix={isCouponEvent(eventId) ? "User " : undefined}
            enableRowSelection={isAdmin && isCouponEvent(eventId)}
            onSendEmailClick={handleSendEmailClick}
            // actionButtons={
            //   // <Button
            //   //   variant="contained"
            //   //   startIcon={<Refresh />}
            //   //   onClick={handleRefresh}
            //   //   size="small"
            //   // >
            //   //   Refresh
            //   // </Button>
            // }
          />
          <EmailPreviewDialog
            open={emailPreviewOpen}
            onClose={() => setEmailPreviewOpen(false)}
            templateType="coupon_success"
            template={couponSuccessTemplate}
            selectedRows={emailPreviewRows}
            onSend={handleSendCouponEmails}
            onSuccess={(msg) =>
              setNotification({ open: true, message: msg, severity: "success" })
            }
            title="Coupon Success Email - Preview"
          />
          {notification.open && (
            <Alert
              severity={notification.severity}
              onClose={() => setNotification({ ...notification, open: false })}
              sx={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
            >
              {notification.message}
            </Alert>
          )}
        </Stack>
      )}
      <Box p={6} />
    </Page>
  );
}

function SearchBox({ onSearch }) {
  const [search, setSearch] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => onSearch(search), 500);
    return () => clearTimeout(t);
  }, [search]);
  return (
    <TextField
      sx={{ maxWidth: 500 }}
      label="Search here..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      InputProps={{
        autoComplete: "new-password",
        endAdornment: (
          <InputAdornment position="end">
            {search && (
              <IconButton onClick={() => setSearch("")}>
                <Backspace />
              </IconButton>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
}

// function CustomFooter({ refetch }) {
//   const apiRef = useGridApiContext();
//   const page = useGridSelector(apiRef, gridPageSelector);
//   const pageCount = useGridSelector(apiRef, gridPageCountSelector);

//   return (
//     <Box
//       sx={{
//         width: "100%",
//         p: 1,
//         display: "flex",
//         justifyContent: "space-between",
//       }}
//     >

//       <Button onClick={() => refetch()} sx={{color:"white"}} variant="outlined" startIcon={<Refresh  size="large" />}>
//       Refresh
//       </Button>

//       <Pagination
//         color="primary"
//         count={pageCount}
//         page={page + 1}
//         onChange={(event, value) => apiRef.current.setPage(value - 1)}

//       />
//     </Box>
//   );
// }
// function CustomFooter({ refetch }) {
//   const apiRef = useGridApiContext();
//   const page = useGridSelector(apiRef, gridPageSelector);
//   const pageCount = useGridSelector(apiRef, gridPageCountSelector);

//   return (
//     <Box
//       sx={{
//         width: "100%",
//         p: 1,
//         display: "flex",
//         justifyContent: "space-between",
//       }}
//     >
//       <Button
//         onClick={() => refetch()}
//         sx={{ color: "white" }}
//         variant="outlined"
//         startIcon={<Refresh size="large" />}
//       >
//         Refresh
//       </Button>

//       <Pagination
//         color="primary"
//         count={pageCount}
//         page={page + 1}
//         onChange={(event, value) => apiRef.current.setPage(value - 1)}
//         showFirstButton
//         showLastButton
//       />
//     </Box>
//   );
// }

// const GET_APPLICANTS = gql`
//   query GetApplicants(
//     $eventId: String!
//     $standId: String!
//     $role: String = "all"
//   ) {
//     applicants(eventId: $eventId, standId: $standId, role: $role) {
//       id
//       data
//       created
//       name
//       type
//       remark
//       role
//     }
//   }
// `;
